/**
 * derivedSkills · pure helper for Progress screen skill derivation.
 *
 * Per Q-D5 c pick (slot_riso_new_screens · 16 MAY 2026): new shared helper
 * extracts skill derivation logic из `SkillsTracker` so Progress.tsx can
 * render Riso "skill-pill" UI с теми же real numbers, без duplication
 * derivation logic и без modifying SkillsTracker.tsx (out of slot scope).
 *
 * SkillsTracker.tsx retains its own internal derivation для backward-compat;
 * Phase G (`slot_riso_cleanup`) can optionally migrate it к this helper.
 *
 * Inputs:
 *   - `progress` from useProgress
 *   - `tickets` from allTickets() · TicketMeta[]
 *
 * Outputs:
 *   - `earnedSkills`: skills с ≥1 closed ticket · sorted by recency
 *   - `inProgressSkills`: skills с pct < 100% и done > 0 · sorted by pct desc
 *
 * Pure function · no React deps · easy to unit-test.
 */

import type { Progress } from '@/types';
import type { TicketMeta } from '@shared/schemas';

/** Display row для earned skill (заходит в Progress «Что ты уже умеешь»). */
export interface EarnedSkillRow {
  /** Skill id (e.g. 'qa-mindset') · used for React key. */
  id: string;
  /** Display label per SKILL_LABELS dict (e.g. 'QA Mindset'). */
  label: string;
  /** Emoji icon per SKILL_EMOJI dict (e.g. '🧠'). */
  emoji: string;
  /** Sprint label где skill earned (most recent done ticket's sprintId, e.g. 'Sprint 02'). */
  sprintLabel: string;
}

/** Display row для in-progress skill. */
export interface InProgressSkillRow {
  id: string;
  label: string;
  emoji: string;
  /** 1-99 · how much of skill's tickets are done. */
  pct: number;
}

/** Result bundle returned by `deriveSkillsFromProgress`. */
export interface DerivedSkills {
  earned: EarnedSkillRow[];
  inProgress: InProgressSkillRow[];
  /** Count of skills с pct === 100 (для prog-section meta). */
  earnedCount: number;
}

/**
 * Skill display dictionaries · mirror those в SkillsTracker.tsx.
 *
 * Kept inline здесь (не importing from SkillsTracker) чтобы избежать circular
 * dep и preserve owner-slot rule. Out-of-sync risk = low (skills evolve slowly);
 * if drift becomes a problem, Phase G can refactor a shared registry.
 */
const SKILL_LABELS: Record<string, string> = {
  // Block 1
  'qa-mindset': 'QA Mindset',
  'qa-concepts': 'QA Concepts',
  'istqb-fundamentals': 'ISTQB Fundamentals',
  'istqb-knowledge': 'ISTQB Knowledge',
  'ticket-writing': 'Ticket Writing',
  reproducibility: 'Reproducibility',
  workflow: 'Workflow & Lifecycle',
  'ai-workflow': 'AI Workflow',
  documentation: 'Documentation',
  'documentation-practice': 'Documentation Practice',
  'test-documentation': 'Test Documentation',
  'testing-levels': 'Testing Levels',
  'testing-approaches': 'Testing Approaches',
  'test-types': 'Test Types',
  'test-cases': 'Test Cases',
  'test-planning': 'Test Planning',
  'test-plan': 'Test Plan',
  'test-design': 'Test Design',
  'requirements-analysis': 'Requirements Analysis',
  requirements: 'Requirements',
  'user-stories': 'User Stories · INVEST',
  'static-testing': 'Static Testing',
  'acceptance-criteria': 'Acceptance Criteria',
  traceability: 'Traceability · RTM',
  rtm: 'RTM',
  'scrum-rituals': 'Scrum Rituals',
  'exploratory-testing': 'Exploratory Testing',
  'non-functional-testing': 'Non-Functional Testing',
  'risk-based-testing': 'Risk-Based Testing',
  'bug-report-writing': 'Bug Report Writing',
  'practice-lab': 'Practice Lab',
  // Block 2
  'bug-lifecycle': 'Bug Lifecycle',
  'bug-reporting': 'Bug Reporting',
  triage: 'Triage',
  evidence: 'Evidence Quality',
  sql: 'SQL',
  'http-api': 'HTTP & API',
  postman: 'Postman',
  'workspace-tools': 'Workspace Tools',
};

const SKILL_EMOJI: Record<string, string> = {
  // Block 1
  'qa-mindset': '🧠',
  'qa-concepts': '📘',
  'istqb-fundamentals': '🎓',
  'istqb-knowledge': '📚',
  'ticket-writing': '✍️',
  reproducibility: '🔁',
  workflow: '🔀',
  'ai-workflow': '🤖',
  documentation: '📄',
  'documentation-practice': '🗂️',
  'test-documentation': '📋',
  'testing-levels': '🪜',
  'testing-approaches': '🎯',
  'test-types': '🏷️',
  'test-cases': '✅',
  'test-planning': '📐',
  'test-plan': '🗺️',
  'test-design': '🧪',
  'requirements-analysis': '🔍',
  requirements: '📝',
  'user-stories': '👤',
  'static-testing': '👀',
  'acceptance-criteria': '☑️',
  traceability: '🧵',
  rtm: '🧮',
  'scrum-rituals': '🌀',
  'exploratory-testing': '🧭',
  'non-functional-testing': '⚙️',
  'risk-based-testing': '⚖️',
  'bug-report-writing': '🐛',
  'practice-lab': '🔬',
  // Block 2
  'bug-lifecycle': '♻️',
  'bug-reporting': '🐞',
  triage: '🏥',
  evidence: '🔬',
  sql: '🗄️',
  'http-api': '🌐',
  postman: '📮',
  'workspace-tools': '🛠️',
};

interface SkillAggregate {
  total: number;
  done: number;
  /** Latest sprintId among done tickets для этого skill (для sprintLabel). */
  latestDoneSprint: string | null;
}

/**
 * Aggregate skills across all tickets · count total + done per skill.
 *
 * @param progress User progress · реad-only
 * @param tickets All ticket metadata · TicketMeta[]
 * @returns Map of skill-id → aggregate
 */
function aggregateSkills(
  progress: Progress,
  tickets: ReadonlyArray<TicketMeta>,
): Map<string, SkillAggregate> {
  const out = new Map<string, SkillAggregate>();
  for (const t of tickets) {
    const isDone = progress.tickets[t.id]?.status === 'done';
    for (const skillId of t.skills) {
      const cur = out.get(skillId) ?? { total: 0, done: 0, latestDoneSprint: null };
      cur.total += 1;
      if (isDone) {
        cur.done += 1;
        // Track latest done sprint by sprintId ord (S02 > S01).
        if (cur.latestDoneSprint === null || t.sprintId > cur.latestDoneSprint) {
          cur.latestDoneSprint = t.sprintId;
        }
      }
      out.set(skillId, cur);
    }
  }
  return out;
}

/**
 * Format sprintId → human label · `S02` → `Sprint 02`.
 */
function formatSprintLabel(sprintId: string): string {
  // Strip leading 'S' и pad to 2 digits.
  const n = sprintId.replace(/^S/, '');
  return `Sprint ${n.padStart(2, '0')}`;
}

/**
 * Main derivation function · pure · takes progress + tickets, returns display rows.
 *
 * @example
 * const { earned, inProgress, earnedCount } = deriveSkillsFromProgress(progress, allTickets());
 */
export function deriveSkillsFromProgress(
  progress: Progress,
  tickets: ReadonlyArray<TicketMeta>,
): DerivedSkills {
  const agg = aggregateSkills(progress, tickets);

  const earned: EarnedSkillRow[] = [];
  const inProgress: InProgressSkillRow[] = [];

  for (const [id, { total, done, latestDoneSprint }] of agg) {
    if (total === 0) continue;
    const pct = Math.round((done / total) * 100);
    const label = SKILL_LABELS[id] ?? id;
    const emoji = SKILL_EMOJI[id] ?? '🎯';

    if (pct === 100 && done > 0) {
      earned.push({
        id,
        label,
        emoji,
        sprintLabel: latestDoneSprint ? formatSprintLabel(latestDoneSprint) : '',
      });
    } else if (done > 0) {
      // Partially earned · pct между 1 и 99.
      inProgress.push({ id, label, emoji, pct });
    }
  }

  // Sort earned by sprintLabel desc (most recent first), tie-break by label.
  earned.sort(
    (a, b) =>
      b.sprintLabel.localeCompare(a.sprintLabel) || a.label.localeCompare(b.label),
  );
  // Sort in-progress by pct desc, tie-break by label.
  inProgress.sort(
    (a, b) => b.pct - a.pct || a.label.localeCompare(b.label),
  );

  return {
    earned,
    inProgress,
    earnedCount: earned.length,
  };
}
